'use client';

import React, { useState } from 'react';
import {
  Plus, Search, MapPin, Clock, DollarSign, Users, ChevronRight,
  Pause, Play, X, Briefcase, Edit2
} from 'lucide-react';
import type { JobPosting, Applicant } from '../page';

interface JobPostingsProps {
  jobs: JobPosting[];
  applicants: Applicant[];
  onViewPipeline: (jobId: string) => void;
  onAddJob: (job: JobPosting) => void;
  onUpdateJob: (job: JobPosting) => void;
}

const STATUS_COLORS: Record<JobPosting['status'], string> = {
  active: 'bg-success/10 text-success border-success/20',
  paused: 'bg-warning/10 text-warning border-warning/20',
  closed: 'bg-muted text-muted-foreground border-border',
};

const TYPE_COLORS: Record<string, string> = {
  'Full-time': 'bg-primary/10 text-primary',
  'Part-time': 'bg-secondary/10 text-secondary',
  'Contract': 'bg-info/10 text-info',
  'Per Diem': 'bg-warning/10 text-warning',
};

const DEPARTMENTS = ['ICU', 'Med-Surg', 'Emergency', 'Urgent Care', 'OR', 'Pediatrics', 'Oncology', 'Rehab'];
const JOB_TYPES: JobPosting['type'][] = ['Full-time', 'Part-time', 'Contract', 'Per Diem'];

const emptyForm = {
  title: '',
  department: 'ICU',
  location: '',
  type: 'Full-time' as JobPosting['type'],
  payRange: '',
  closingDate: '',
  description: '',
  requirements: '',
};

export default function JobPostings({ jobs, applicants, onViewPipeline, onAddJob, onUpdateJob }: JobPostingsProps) {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | JobPosting['status']>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<JobPosting | null>(null);
  const [form, setForm] = useState(emptyForm);

  const filtered = jobs.filter((j) => {
    const matchSearch =
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      j.department.toLowerCase().includes(search.toLowerCase()) ||
      j.location.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || j.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const openCreate = () => {
    setEditingJob(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (job: JobPosting) => {
    setEditingJob(job);
    setForm({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      payRange: job.payRange,
      closingDate: job.closingDate,
      description: job.description,
      requirements: job.requirements.join('\n'),
    });
    setShowModal(true);
  };

  const handleSubmit = () => {
    if (!form.title.trim() || !form.location.trim()) return;
    const reqs = form.requirements
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean);

    if (editingJob) {
      onUpdateJob({
        ...editingJob,
        title: form.title,
        department: form.department,
        location: form.location,
        type: form.type,
        payRange: form.payRange,
        closingDate: form.closingDate,
        description: form.description,
        requirements: reqs,
      });
    } else {
      const newJob: JobPosting = {
        id: `job-${Date.now()}`,
        title: form.title,
        department: form.department,
        location: form.location,
        type: form.type,
        status: 'active',
        postedDate: new Date().toISOString().split('T')[0],
        closingDate: form.closingDate,
        applicants: 0,
        newApplicants: 0,
        description: form.description,
        requirements: reqs,
        payRange: form.payRange,
      };
      onAddJob(newJob);
    }
    setShowModal(false);
  };

  const toggleStatus = (job: JobPosting) => {
    const next: JobPosting['status'] = job.status === 'active' ? 'paused' : 'active';
    onUpdateJob({ ...job, status: next });
  };

  const getApplicantsByStage = (jobId: string) => {
    const jobApps = applicants.filter((a) => a.jobId === jobId);
    return {
      applied: jobApps.filter((a) => a.stage === 'applied').length,
      screening: jobApps.filter((a) => a.stage === 'screening').length,
      interview: jobApps.filter((a) => a.stage === 'interview').length,
      offer: jobApps.filter((a) => a.stage === 'offer').length,
      hired: jobApps.filter((a) => a.stage === 'hired').length,
    };
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search jobs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex gap-1 bg-muted p-1 rounded-lg">
          {(['all', 'active', 'paused', 'closed'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-500 capitalize transition-all ${
                filterStatus === s ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-500 hover:bg-primary/90 transition-colors"
        >
          <Plus size={14} />
          Post Job
        </button>
      </div>

      {/* Job Cards */}
      <div className="grid gap-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Briefcase size={32} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No job postings found</p>
          </div>
        )}
        {filtered.map((job) => {
          const stages = getApplicantsByStage(job.id);
          return (
            <div key={job.id} className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-sm font-600 text-foreground">{job.title}</h3>
                    <span className={`text-[10px] font-600 px-2 py-0.5 rounded-full border ${STATUS_COLORS[job.status]} capitalize`}>
                      {job.status}
                    </span>
                    <span className={`text-[10px] font-500 px-2 py-0.5 rounded-full ${TYPE_COLORS[job.type] ?? 'bg-muted text-muted-foreground'}`}>
                      {job.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1"><Briefcase size={11} />{job.department}</span>
                    <span className="flex items-center gap-1"><MapPin size={11} />{job.location}</span>
                    <span className="flex items-center gap-1"><DollarSign size={11} />{job.payRange}</span>
                    <span className="flex items-center gap-1"><Clock size={11} />Closes {job.closingDate}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEdit(job)}
                    className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                    title="Edit job"
                  >
                    <Edit2 size={13} />
                  </button>
                  {job.status !== 'closed' && (
                    <button
                      onClick={() => toggleStatus(job)}
                      className="p-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      title={job.status === 'active' ? 'Pause' : 'Activate'}
                    >
                      {job.status === 'active' ? <Pause size={13} /> : <Play size={13} />}
                    </button>
                  )}
                  <button
                    onClick={() => onViewPipeline(job.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-500 hover:bg-primary/20 transition-colors"
                  >
                    <Users size={12} />
                    {job.applicants} Applicants
                    {job.newApplicants > 0 && (
                      <span className="bg-primary text-primary-foreground text-[9px] font-700 rounded-full px-1.5 py-0.5">
                        +{job.newApplicants}
                      </span>
                    )}
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>

              {/* Pipeline mini-bar */}
              <div className="mt-4 flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground w-14 flex-shrink-0">Pipeline</span>
                <div className="flex items-center gap-1.5 flex-1">
                  {[
                    { label: 'Applied', count: stages.applied, color: 'bg-muted-foreground/40' },
                    { label: 'Screening', count: stages.screening, color: 'bg-info/60' },
                    { label: 'Interview', count: stages.interview, color: 'bg-warning/60' },
                    { label: 'Offer', count: stages.offer, color: 'bg-primary/60' },
                    { label: 'Hired', count: stages.hired, color: 'bg-success/60' },
                  ].map((s) => (
                    <div key={s.label} className="flex items-center gap-1">
                      <div className={`w-1.5 h-1.5 rounded-full ${s.color}`} />
                      <span className="text-[10px] text-muted-foreground">{s.label} <span className="font-600 text-foreground">{s.count}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-600 text-foreground">
                {editingJob ? 'Edit Job Posting' : 'Post New Job'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Job Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Registered Nurse – ICU"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Department</label>
                  <select
                    value={form.department}
                    onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Job Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as JobPosting['type'] }))}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Location *</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder="City, State"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Pay Range</label>
                  <input
                    type="text"
                    value={form.payRange}
                    onChange={(e) => setForm((f) => ({ ...f, payRange: e.target.value }))}
                    placeholder="e.g. $65–$82/hr"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Closing Date</label>
                <input
                  type="date"
                  value={form.closingDate}
                  onChange={(e) => setForm((f) => ({ ...f, closingDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Job Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe the role, responsibilities, and team..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Requirements (one per line)</label>
                <textarea
                  value={form.requirements}
                  onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))}
                  rows={4}
                  placeholder="Active RN License&#10;BLS/ACLS Certified&#10;2+ years ICU experience"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg border border-border bg-card text-sm font-500 text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.title.trim() || !form.location.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingJob ? 'Save Changes' : 'Post Job'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
